import React from 'react';
import MetricsCard, { MetricItem, MetricGrid } from './shared/MetricsCard';
import { COLORS } from '../constants/theme';

/**
 * Operational Dashboard Component
 * Forsyning Danmark Professional Theme
 */
const OperationalDashboard = ({ stats = {}, config = {}, currentHour = 0 }) => {
  // Calculate operational efficiency metrics
  const calculateEfficiency = () => {
    const totalHours = 8783;
    const completedHours = Math.min(currentHour, totalHours);
    
    // Gennemsnitspriser kun for operational
    const avgSolarPrice = stats.operationalSolarPurchased > 0 ?
      (stats.operationalSolarCost || 0) / (stats.operationalSolarPurchased || 1) : 0;
    
    const avgGridPrice = stats.operationalGridPurchased > 0 ?
      (stats.operationalGridCost || 0) / (stats.operationalGridPurchased || 1) : 0;
    
    // Gennemsnitspris for el til varme (kun sol + grid operational)
    const totalOperationalElectricity = (stats.operationalSolarPurchased || 0) + (stats.operationalGridPurchased || 0);
    const totalOperationalElCost = (stats.operationalSolarCost || 0) + (stats.operationalGridCost || 0);
    const avgElToHeatPrice = totalOperationalElectricity > 0 ? 
      totalOperationalElCost / totalOperationalElectricity : 0;
    
    // Samlet gennemsnitspris for alle 3 energikilder (sol + grid + overskudsvarme)
    const totalAllEnergy = totalOperationalElectricity + (stats.operationalFactoryWasteHeatUsed || 0);
    const totalAllCost = totalOperationalElCost + (stats.operationalFactoryWasteHeatCost || 0);
    const avgAllSourcesPrice = totalAllEnergy > 0 ? totalAllCost / totalAllEnergy : 0;
    
    return {
      heaterUtilization: stats.fullPurchaseHours > 0 ? 
        ((stats.fullPurchaseHours + stats.partialPurchaseHours) / completedHours * 100) : 0,
      solarUtilization: stats.solarAvailable > 0 ?
        ((stats.operationalSolarPurchased + stats.arbitrageSolarPurchased) / stats.solarAvailable * 100) : 0,
      wasteHeatUtilization: (stats.wasteHeatAvailable || 17568) > 0 ?
        (stats.wasteHeatAccepted / (stats.wasteHeatAvailable || 17568) * 100) : 0,
      turbineHours: stats.turbineOperatingHours || 0,
      batteryCycles: stats.batteryCycles?.chargeCycles || stats.batteryCycles || 0,
      storageEfficiency: stats.storageEfficiency || 95,
      avgSolarPrice: avgSolarPrice,
      avgGridPrice: avgGridPrice,
      avgElToHeatPrice: avgElToHeatPrice,
      avgAllSourcesPrice: avgAllSourcesPrice
    };
  };
  
  const efficiency = calculateEfficiency();

  // El-kedel timer beregning - bruger nu heaterEffectiveHours fra simulering
  // HELIAC FLOW: Al el → Kedel → HTES → Turbine (85% varme / 15% el) → LTES → Demand
  const calculateBoilerHours = () => {
    const boilerCapacity = config.heater_MW || 30; // MW kapacitet (kan være 15, 20, 30 MW)

    // TOTAL EL KØBT TIL VARME = operationalGridPurchased (det eneste rigtige køb!)
    // Batteri arbitrage er separat og tælles IKKE her
    const totalElPurchased = (stats.operationalGridPurchased || 0) / 1000; // GWh

    // VARME PRODUCERET = htesTotalHeatProduced (turbine 85% split)
    // Dette er den faktiske varme der leveres til LTES/demand
    const heatProduced = (stats.htesTotalHeatProduced || 0) / 1000; // GWh

    // Use heaterEffectiveHours from simulation if available (more accurate EFLH)
    // Falls back to calculated hours if not available
    const calculatedHours = Math.round((totalElPurchased * 1000) / boilerCapacity);
    const actualBoilerHours = stats.heaterEffectiveHours || calculatedHours || 0;

    // Timer til systemydelser = Resten af året
    const systemServiceHours = Math.max(0, 8783 - actualBoilerHours);

    // Fordeling af systemydelser (kun mFRR og aFRR - ikke FCR-D)
    const mFRRHours = Math.round(systemServiceHours * 0.67); // 67% til mFRR
    const aFRRHours = Math.round(systemServiceHours * 0.33); // 33% til aFRR

    return {
      boilerCapacity: boilerCapacity || 20,
      operationalEl: totalElPurchased || 0,  // Total el købt (via HTES)
      heatProduced: heatProduced || 0,        // Varme fra turbine
      totalElPurchased: totalElPurchased || 0,
      actualBoilerHours: actualBoilerHours || 0,
      systemServiceHours: systemServiceHours || 8783,
      mFRRHours: mFRRHours || 0,
      aFRRHours: aFRRHours || 0,
      boilerPercent: ((actualBoilerHours || 0) / 8783 * 100).toFixed(1),
      systemPercent: ((systemServiceHours || 8783) / 8783 * 100).toFixed(1)
    };
  };

  const boilerMetrics = calculateBoilerHours();
  
  // Ultra-compact grid for C-level dashboards
  const getGridColumns = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1920) return 'repeat(6, 1fr)';
      if (window.innerWidth >= 1600) return 'repeat(5, 1fr)';
      if (window.innerWidth >= 1440) return 'repeat(4, 1fr)';
      if (window.innerWidth >= 1024) return 'repeat(3, 1fr)';
    }
    return 'repeat(auto-fit, minmax(200px, 1fr))';
  };
  
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: getGridColumns(),
      gap: '0.25rem',
      padding: '0.25rem',
      background: COLORS.ui.background,
      fill: 'fill',
    }}>
      
      {/* === PRODUKTION KATEGORI === */}
      {/* RØD TRÅD: An-værk (input) → Ab-værk (-3%) → Net (-10%) */}
      <MetricsCard
        title="Varmeproduktion"
        subtitle="An-værk→Ab-værk→Net"
        color={COLORS.operational.production}
        icon="🔥"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.operational.production}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="An-værk (input)"
            value={((stats.anPlantProduction || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`Gns. ${(stats.operationalGridPurchased > 0 ? (stats.operationalGridCost / stats.operationalGridPurchased).toFixed(0) : "0")} DKK/MWh inkl. TSO`}
            size="small"
            highlight
          />
          <MetricItem
            label="Ab-værk"
            value={((stats.abPlantDelivery || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Efter 3% produktionstab"
            size="small"
          />
          <MetricItem
            label="Netlevering"
            value={((stats.netDelivery || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Efter 10% grid loss"
            size="small"
            highlight
          />
          <MetricItem
            label="El-kedel timer"
            value={(boilerMetrics.actualBoilerHours || 0).toFixed(0)}
            unit="timer"
            detail={`${boilerMetrics.boilerPercent}% aktiv`}
            size="small"
          />
          <MetricItem
            label="Turbine timer"
            value={(stats.turbinePartialHours || 0).toFixed(0)}
            unit="timer"
            detail={`${((stats.turbinePartialHours || 0) / 8783 * 100).toFixed(1)}% aktiv`}
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
      {/* === ENERGIKILDER KATEGORI === */}
      {/* RØD TRÅD: El købt total → 85% varme + 15% el */}
      <MetricsCard
        title="Energikilder"
        subtitle="El købt → Turbine split"
        color={COLORS.primary.green}
        icon="⚡"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.primary.green}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="El købt total"
            value={(((stats.htesTotalHeatProduced || 0) + (stats.htesTotalElProduced || 0)) / 1000).toFixed(1)}
            unit="GWh"
            detail={`Købt ${(stats.operationalGridPurchased > 0 ? (stats.operationalGridCost / stats.operationalGridPurchased).toFixed(0) : "0")} DKK/MWh`}
            size="small"
            highlight
          />
          <MetricItem
            label="→ Varme (85%)"
            value={((stats.htesTotalHeatProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Til An-værk"
            size="small"
          />
          <MetricItem
            label="→ El solgt (15%)"
            value={((stats.htesTotalElProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`Solgt ${(stats.htesTotalElProduced > 0 ? (stats.htesTotalElRevenue / stats.htesTotalElProduced).toFixed(0) : "0")} DKK/MWh`}
            size="small"
          />
          <MetricItem
            label="Overskudsvarme"
            value={((stats.wasteHeatAccepted || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="150 DKK/MWh (direkte LTES)"
            size="small"
          />
          <MetricItem
            label="Sol (via HTES)"
            value={((stats.solarPurchased || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${efficiency.avgSolarPrice.toFixed(0)} DKK/MWh`}
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
      

      {/* === HTES LAGER === */}
      {/* RØD TRÅD: Total input = Varme (85%) + El (15%) fra turbine */}
      <MetricsCard
        title="HTES Lager"
        subtitle="Turbine: 85% varme / 15% el"
        color={COLORS.operational.htes}
        icon="🔥"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.operational.htes}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="HTES input total"
            value={(((stats.htesTotalHeatProduced || 0) + (stats.htesTotalElProduced || 0)) / 1000).toFixed(1)}
            unit="GWh"
            detail={`Gns. ${(stats.operationalGridPurchased > 0 ? (stats.operationalGridCost / stats.operationalGridPurchased).toFixed(0) : "0")} DKK/MWh køb`}
            size="small"
            highlight
          />
          <MetricItem
            label="→ Varme (85%)"
            value={((stats.htesTotalHeatProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Til An-værk"
            size="small"
          />
          <MetricItem
            label="→ El solgt (15%)"
            value={((stats.htesTotalElProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`Gns. ${(stats.htesTotalElProduced > 0 ? (stats.htesTotalElRevenue / stats.htesTotalElProduced).toFixed(0) : "0")} DKK/MWh`}
            size="small"
          />
          <MetricItem
            label="Turbine margin"
            value={stats.htesTotalElProduced > 0 && stats.operationalGridPurchased > 0 ?
              ((stats.htesTotalElRevenue / stats.htesTotalElProduced) - (stats.operationalGridCost / stats.operationalGridPurchased)).toFixed(0) : "0"}
            unit="DKK/MWh"
            detail={`Køb ${(stats.operationalGridPurchased > 0 ? (stats.operationalGridCost / stats.operationalGridPurchased).toFixed(0) : "0")} → Salg ${(stats.htesTotalElProduced > 0 ? (stats.htesTotalElRevenue / stats.htesTotalElProduced).toFixed(0) : "0")}`}
            color={((stats.htesTotalElRevenue || 0) / (stats.htesTotalElProduced || 1)) > ((stats.operationalGridCost || 0) / (stats.operationalGridPurchased || 1)) ?
              COLORS.status.success : COLORS.status.error}
            highlight
            size="small"
          />
        </MetricGrid>
      </MetricsCard>

      {/* === TURBINE OPERATION === */}
      <MetricsCard
        title="Turbine Drift"
        subtitle="Samlet el-produktion"
        color={COLORS.electricity}
        icon="⚙️"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.electricity}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Total el"
            value={((stats.turbineElectricityProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.turbineRevenue || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Drift timer"
            value={(stats.turbinePartialHours || 0).toFixed(0)}
            unit="timer"
            detail={`${stats.turbineOperatingHours || 0} aktiveringer`}
            size="small"
          />
          <MetricItem
            label="Serviceindtægt"
            value={((stats.turbineServiceRevenue || 0) / 1000000).toFixed(2)}
            unit="M DKK"
            detail={`${stats.turbineServiceHours || 0} timer`}
            size="small"
          />
          <MetricItem
            label="Gns. effekt"
            value={stats.turbineOperatingHours > 0 ?
              ((stats.turbineElectricityProduced || 0) * 1000 / (stats.turbineOperatingHours || 1)).toFixed(1) : "0"}
            unit="kW"
            detail="El output pr. aktivering"
            size="small"
          />
        </MetricGrid>
      </MetricsCard>

      {/* === BATTERI ARBITRAGE === */}
      <MetricsCard
        title="Batteri Arbitrage"
        subtitle="Multi-cycle optimering"
        color={COLORS.battery}
        icon="🔋"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.battery}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Ladet (input)"
            value={((stats.batteryChargeAmount || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.batteryChargeCost || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Afladet"
            value={((stats.batteryElectricitySold || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.batteryRevenue || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Cykler"
            value={((stats.batteryChargeAmount || 0) / 3).toFixed(0)}
            unit="total"
            detail={`${((stats.batteryChargeAmount || 0) / 3 / 366).toFixed(1)} per dag`}
            size="small"
          />
          <MetricItem
            label="Efficiency"
            value={stats.batteryChargeAmount > 0 ?
              ((stats.batteryElectricitySold || 0) / (stats.batteryChargeAmount || 1) * 100).toFixed(1) : "0"}
            unit="%"
            detail="Round-trip efficiency"
            size="small"
          />
          <MetricItem
            label="Profit"
            value={(((stats.batteryRevenue || 0) - (stats.batteryChargeCost || 0)) / 1000000).toFixed(2)}
            unit="M DKK"
            detail={`${stats.batteryElectricitySold > 0 ?
              (((stats.batteryRevenue || 0) - (stats.batteryChargeCost || 0)) /
              (stats.batteryElectricitySold || 1)).toFixed(0) : "0"} DKK/MWh margin`}
            color={(stats.batteryRevenue || 0) > (stats.batteryChargeCost || 0) ?
              COLORS.status.success : COLORS.status.error}
            size="small"
          />
          <MetricItem
            label="Serviceindtægt"
            value={((stats.batteryServiceRevenue || 0) / 1000000).toFixed(2)}
            unit="M DKK"
            detail={`${stats.batteryServiceHours || 0} timer`}
            size="small"
          />
          <MetricItem
            label="Samlet profit"
            value={((((stats.batteryRevenue || 0) - (stats.batteryChargeCost || 0)) + (stats.batteryServiceRevenue || 0)) / 1000000).toFixed(2)}
            unit="M DKK"
            detail="Arbitrage + service"
            color={(((stats.batteryRevenue || 0) - (stats.batteryChargeCost || 0)) + (stats.batteryServiceRevenue || 0)) > 0 ?
              COLORS.status.success : COLORS.status.error}
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
      {/* === LEVERANCE === */}
      <MetricsCard
        title="Leverance"
        subtitle="Kundeforsyning"
        color={COLORS.status.success}
        icon="✅"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.status.success}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Netlevering"
            value={((stats.netDelivery || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Efter 10% tab"
            size="small"
          />
          <MetricItem
            label="Dækning"
            value="100.0"
            unit="%"
            detail="0 timer uden"
            size="small"
          />
          <MetricItem
            label="Effektivitet"
            value={stats.netDelivery && stats.anPlantProduction ? 
              ((stats.netDelivery / stats.anPlantProduction) * 100).toFixed(1) : "0.0"}
            unit="%"
            detail="Output/Input"
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
      {/* === DETALJERET ELKØB === */}
      <MetricsCard
        title="Elkøb fra net"
        subtitle= "Varmeproduktion og Arbitrage"
        color={COLORS.electricity}
        icon="⚡"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.electricity}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Varmeproduktion"
            value={((stats.operationalGridPurchased || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.operationalGridCost || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Arbitrage"
            value={((stats.arbitrageGridPurchased || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.arbitrageGridCost || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Gns. pris"
            value={efficiency.avgGridPrice.toFixed(0)}
            unit="DKK/MWh"
            detail="Inkl. TSO"
            size="small"
          />
        </MetricGrid>
      </MetricsCard>

      {/* === SOLENERGI === */}
      <MetricsCard
        title="Solenergi"
        subtitle="Solen til varmeproduktion og arbitrage"
        color={COLORS.solar}
        icon="☀️"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.solar}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Varmeproduktion"
            value={((stats.operationalSolarPurchased || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.operationalSolarCost || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Arbitrage"
            value={((stats.arbitrageSolarPurchased || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.arbitrageSolarCost || 0) / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Gns. pris"
            value={efficiency.avgSolarPrice.toFixed(0)}
            unit="DKK/MWh"
            detail="Uden TSO"
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
      {/* === OVERSKUDSVARME === */}
      <MetricsCard
        title="Overskudsvarme"
        subtitle="Overskudsvarme fra fabrikker"
        color={COLORS.waste}
        icon="♻️"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.waste}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Accepteret"
            value={((stats.wasteHeatAccepted || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${((stats.wasteHeatAccepted || 0) * 150 / 1000000).toFixed(2)} M DKK`}
            size="small"
          />
          <MetricItem
            label="Tilgængelig"
            value={((stats.wasteHeatAvailable || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail={`${efficiency.wasteHeatUtilization.toFixed(1)}% brugt`}
            size="small"
          />
          <MetricItem
            label="Fast pris"
            value="150"
            unit="DKK/MWh"
            detail="Kontrakt"
            size="small"
          />
        </MetricGrid>
      </MetricsCard>

      {/* === PRODUKTION & TAB === */}
      {/* RØD TRÅD: An-værk → Ab-værk (-3%) → Net (-10%) */}
      <MetricsCard
        title="Energiflow & Tab"
        subtitle="An-værk → Ab-værk → Net"
        color={COLORS.operational.anPlant}
        icon="📊"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.operational.anPlant}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Turbine varme (85%)"
            value={((stats.htesTotalHeatProduced || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Fra HTES → An-værk"
            size="small"
          />
          <MetricItem
            label="Overskudsvarme"
            value={((stats.operationalFactoryWasteHeatUsed || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Direkte til An-værk"
            color={COLORS.waste}
            size="small"
          />
          <MetricItem
            label="An-værk (input)"
            value={((stats.anPlantProduction || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Turbine + overskud"
            size="small"
            highlight
          />
          <MetricItem
            label="Produktionstab"
            value={((stats.productionLosses || 0) / 1000).toFixed(2)}
            unit="GWh"
            detail="3% An→Ab tab"
            color={COLORS.status.error}
            size="small"
          />
          <MetricItem
            label="Ab-værk (output)"
            value={((stats.abPlantDelivery || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Til fjernvarmenet"
            size="small"
          />
          <MetricItem
            label="Grid tab"
            value={((stats.gridLosses || 0) / 1000).toFixed(2)}
            unit="GWh"
            detail="10% net tab"
            color={COLORS.status.error}
            size="small"
          />
          <MetricItem
            label="Netlevering"
            value={((stats.netDelivery || 0) / 1000).toFixed(1)}
            unit="GWh"
            detail="Til kunder"
            highlight
            size="small"
          />
        </MetricGrid>
      </MetricsCard>

      {/* === SYSTEMSTATUS === */}
      <MetricsCard
        title="Systemstatus"
        subtitle={`Time ${currentHour} / 8783`}
        color={COLORS.status.info}
        icon="📊"
        size="small"
        style={{ borderTop: `2px solid ${COLORS.status.info}` }}
      >
        <MetricGrid columns={1}>
          <MetricItem
            label="Elkedel"
            value={config.heater_MW || 30}
            unit="MW"
            size="small"
          />
          <MetricItem
            label="Turbine"
            value={config.turbine_MW || 10}
            unit="MW"
            size="small"
          />
          <MetricItem
            label="HTES slut"
            value={(stats.finalHTESLevel || 0).toFixed(0)}
            unit="%"
            detail={`${((stats.finalHTESLevel || 0) / 100 * (config.htes_capacity_MWh || 300)).toFixed(0)} MWh`}
            size="small"
          />
          <MetricItem
            label="LTES slut"
            value={(stats.finalLTESLevel || 0).toFixed(0)}
            unit="%"
            detail={`${((stats.finalLTESLevel || 0) / 100 * (config.ltes_capacity_MWh || 205)).toFixed(0)} MWh`}
            size="small"
          />
          <MetricItem
            label="Progress"
            value={(currentHour / 8783 * 100).toFixed(1)}
            unit="%"
            detail={`${Math.floor(currentHour / 24)} dage`}
            size="small"
          />
        </MetricGrid>
      </MetricsCard>
      
    </div>
  );
};

export default OperationalDashboard;